import React, { ChangeEvent, useState, useEffect } from "react";
import { MultipleFileUploadComponent } from "components/form/MultipleFileUpload";
import { BasePopup } from "components/pop-up/BasePopup";
import { Referral } from "types";
import { updateAssetReferral } from "services/assetRecoveryService";
import { useTooltip } from "context/TooltipProvider";
import { useTranslation } from "react-i18next";

interface CreateReinitiateRequestPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (e: React.FormEvent) => void;
  isEditMode?: boolean;
  existingData?: any;
  referralId: string;
  referral: Referral | null;
  onReinitiateCreated?: (res: any) => void;
  setActiveTab?: React.Dispatch<React.SetStateAction<string>>;
}

const defaultFormData = {
  reinitiate_reseaon_type: "",
  reinitiate_request_summary: "",
  reinitiate_request_date: "",
};

export const CreateReinitiateRequestPopup: React.FC<
  CreateReinitiateRequestPopupProps
> = ({
  isOpen,
  onClose,
  onSubmit,
  isEditMode = false,
  existingData,
  referralId,
  setActiveTab,
  onReinitiateCreated,
}) => {
  const [loading, setLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [formData, setFormData] = useState(defaultFormData);

  const { showTooltip } = useTooltip();
  const { t } = useTranslation();

  const actualEditMode = isEditMode || Boolean(existingData);

  useEffect(() => {
    if (isOpen && actualEditMode && existingData) {
      console.log("Initializing edit mode with data:", existingData);
      // Populate form with existing data
      setFormData({
        reinitiate_reseaon_type: existingData.reinitiate_reseaon_type || "",
        reinitiate_request_summary:
          existingData.reinitiate_request_summary || "",
        reinitiate_request_date: existingData.reinitiate_request_date || "",
      });
    } else if (isOpen && !actualEditMode) {
      // Reset form for new entry
      console.log("Initializing create mode");
      resetForm();
    }
  }, [isOpen, actualEditMode, existingData]);

  const resetForm = () => {
    setFormData(defaultFormData);
    setUploadedFiles([]);
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.reinitiate_reseaon_type.trim()) {
      showTooltip(t("please_select_reinitiate_reason_type"), "error");
      return;
    }

    if (!formData.reinitiate_request_summary.trim()) {
      showTooltip(t("please_enter_reinitiate_request_summary"), "error");
      return;
    }

    if (!formData.reinitiate_request_date) {
      showTooltip(t("please_select_reinitiate_request_date"), "error");
      return;
    }

    setLoading(true);

    try {
      const data = new FormData();

      // Append form data
      data.append("reinitiate_reseaon_type", formData.reinitiate_reseaon_type);
      data.append(
        "reinitiate_request_summary",
        formData.reinitiate_request_summary,
      );
      data.append("reinitiate_request_date", formData.reinitiate_request_date);

      // Set appropriate status based on edit mode
      if (actualEditMode) {
        data.append("action_status", "Reinitiate Updated");
      } else {
        data.append("action_status", "Reinitiate Requested");
      }

      // Append files
      if (uploadedFiles.length > 0) {
        uploadedFiles.forEach((file) => {
          data.append("reinitiate_request_document", file);
        });
      }

      // Update the referral with reinitiate data
      const response = await updateAssetReferral(referralId, data);

      const successMessage = actualEditMode
        ? t("reinitiate_request_updated_successfully")
        : t("reinitiate_request_created_successfully");

      showTooltip(successMessage, "success");
      onClose();
      resetForm();

      if (onReinitiateCreated && response) {
        onReinitiateCreated(response);
        setActiveTab?.("Referral");
      }
    } catch (error) {
      const errorMessage = actualEditMode
        ? t("failed_to_update_reinitiate_request")
        : t("failed_to_create_reinitiate_request");
      showTooltip(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <BasePopup
      isOpen={isOpen}
      onClose={handleClose}
      title={
        actualEditMode ? "Edit Reinitiate Request" : "Create Reinitiate Request"
      }
      description={
        actualEditMode
          ? "Edit reinitiate request information"
          : "Create a new reinitiate request for this referral"
      }
    >
      <form onSubmit={handleSubmit}>
        <div className="flex flex-col">
          <label className="referral-popup-label">
            Reinitiate Reason Type *
          </label>
          <input
            className="referral-form-field"
            onChange={handleInputChange}
            name="reinitiate_reseaon_type"
            value={formData.reinitiate_reseaon_type}
            placeholder="Enter reinitiate reason type"
          />
        </div>

        <div className="flex flex-col mt-4">
          <label className="referral-popup-label">
            Reinitiate Request Summary *
          </label>
          <textarea
            className="referral-form-field h-24"
            onChange={handleInputChange}
            name="reinitiate_request_summary"
            placeholder="Enter detailed reinitiate request summary..."
            value={formData.reinitiate_request_summary}
          />
        </div>

        <div className="flex flex-col mt-4">
          <label className="referral-popup-label">
            Reinitiate Request Date *
          </label>
          <input
            className="referral-form-field"
            onChange={handleInputChange}
            name="reinitiate_request_date"
            type="date"
            value={formData.reinitiate_request_date}
          />
        </div>

        <div className="mt-4">
          <MultipleFileUploadComponent
            uploadedFiles={uploadedFiles}
            onFilesChange={setUploadedFiles}
            label="Reinitiate Request Documents"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className={`mt-6 mb-4 referral-form-button text-white w-full ${
            loading ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          {loading
            ? "Processing..."
            : actualEditMode
              ? "Update Reinitiate Request"
              : "Create Reinitiate Request"}
        </button>
      </form>
    </BasePopup>
  );
};

export default CreateReinitiateRequestPopup;
